/**
 * What a station is offering.
 *
 * The rules — what a band pays, how long it allows, what a crate weighs — are
 * in `economy/missions.ts` and testable without a galaxy. This is the half that
 * has to walk one: finding somewhere to send you, which means knowing what is
 * within two jumps, what is across the cluster, and what is a crossing away.
 *
 * ── Why a board rotates but does not shuffle ────────────────────────────────
 *
 * Seeded off the station *and the hour*. Off the station alone, a board would
 * be the same three jobs forever and a station you had cleared would never be
 * worth returning to. Rolled fresh per visit, it would reshuffle while you were
 * reading it and the job you were deciding about would vanish mid-thought.
 *
 * An hour is long enough to fly somewhere and come back to a changed board, and
 * far longer than anyone spends staring at one.
 */
import { CLUSTER_JUMP_CEILING, Navigator } from '../world/navigation.js';
import { RESTOCK, worldTime } from '../world/clock.js';
import { Rng } from '../world/rng.js';
import { getSystem } from '../world/generate.js';
import { rollMission, tutorialOffer } from './missions.js';
/** How many offers a board carries. */
const OFFERS = 4;
/**
 * How often the board turns over.
 *
 * The same window everything else a station offers uses — its shelf of goods,
 * its plating, its paint. One clock per station rather than one per counter:
 * "come back in a couple of hours" should be a thing you learn about a place,
 * not about each desk in it.
 */
const ROTATION = RESTOCK;
/**
 * Which kinds a band offers.
 *
 * Data runs are the low-value job and crates are the middling one, so the short
 * hops are mostly data and the long hauls are mostly crates — you do not send a
 * weightless drive across a cluster boundary and pay three hundred for it.
 */
const KINDS = {
    close: ['data', 'data', 'crate'],
    far: ['crate', 'crate', 'data'],
    distant: ['crate', 'crate', 'crate'],
};
/** Jumps within which a destination counts as `close`. */
const CLOSE_JUMPS = 2;
/**
 * Everything reachable in `CLOSE_JUMPS` hops or fewer.
 *
 * Walked hop by hop, because "within two jumps" is not "within twice the jump
 * range" — and taking the second for the first is what put three-jump systems
 * on the close board. Two systems can sit one range apart in a straight line
 * and still need three hops if there is nothing in between to stand on.
 *
 * Measured against the *basic* range rather than the player's, because a band
 * is a property of the job. A better drive should make a close job quicker, not
 * turn it into a different job.
 */
function within(navigator, from, hops) {
    const seen = new Set([from]);
    let frontier = [from];
    for (let hop = 0; hop < hops; hop++) {
        const next = [];
        for (const id of frontier) {
            for (const system of navigator.neighbours(id)) {
                if (seen.has(system.id))
                    continue;
                seen.add(system.id);
                next.push(system.id);
            }
        }
        frontier = next;
    }
    seen.delete(from);
    return seen;
}
function stationsIn(seed, systemId) {
    const system = getSystem(seed, systemId);
    return system.stations.map((station) => ({
        systemId,
        stationId: station.id,
        systemName: system.name,
        stationName: station.name,
    }));
}
/**
 * Somewhere to send a job of this band.
 *
 * Candidate *systems* come from summaries, which carry a station count without
 * generating anything — so the expensive step happens once, on the one system
 * actually chosen.
 */
function pickDestination(seed, navigator, from, band, random) {
    const here = navigator.get(from);
    if (!here)
        return null;
    let candidates = [];
    if (band === 'distant') {
        // Any system in a neighbouring cluster. The route planner works out how
        // many crossings that actually costs; the band only promises "at least one".
        const clusters = navigator.warpNeighbours(here.clusterId, CLUSTER_JUMP_CEILING);
        candidates = clusters.flatMap(({ id }) => navigator.systemsIn(id).filter((system) => system.stationCount > 0).map((system) => system.id));
    }
    else {
        const near = within(navigator, from, CLOSE_JUMPS);
        const local = navigator
            .systemsIn(here.clusterId)
            .filter((system) => system.stationCount > 0 && system.id !== from);
        candidates =
            band === 'close'
                ? local.filter((system) => near.has(system.id)).map((system) => system.id)
                : local.filter((system) => !near.has(system.id)).map((system) => system.id);
        // A close job can also be a hop across this system, station to station.
        if (band === 'close')
            candidates = [from, ...candidates];
    }
    if (candidates.length === 0)
        return null;
    const systemId = candidates[Math.min(candidates.length - 1, Math.floor(random.float() * candidates.length))];
    const stations = stationsIn(seed, systemId);
    if (stations.length === 0)
        return null;
    return stations[Math.min(stations.length - 1, Math.floor(random.float() * stations.length))];
}
/**
 * The jobs on offer here, now.
 *
 * Every band is attempted; one that has nowhere to send you simply does not
 * appear. A cluster with no neighbour in range offers no distant work, which is
 * true rather than a gap.
 */
export function stationMissions(seed, systemId, stationId, now = Date.now()) {
    const navigator = new Navigator(seed);
    const era = Math.floor(worldTime(now) / ROTATION);
    const bands = ['close', 'close', 'far', 'distant'];
    const offers = [];
    for (let slot = 0; slot < OFFERS; slot++) {
        const band = bands[slot] ?? 'close';
        const random = Rng.forEntity(seed, stationId, `mission-${era}-${slot}`);
        const destination = pickDestination(seed, navigator, systemId, band, random);
        if (!destination)
            continue;
        // Never a job to the station you are standing in — that is not a delivery.
        if (destination.stationId === stationId)
            continue;
        const kinds = KINDS[band];
        const kind = kinds[Math.min(kinds.length - 1, Math.floor(random.float() * kinds.length))];
        offers.push(rollMission(random, { id: `${stationId}-${era}-${slot}`, kind, band, destination }));
    }
    return offers;
}
/**
 * The board a pilot who is still being introduced sees.
 *
 * Exactly one job, and it is the introduction's delivery. A first mission board
 * with four offers on it is a decision; this is meant to be an instruction, and
 * the whole of what it asks is "take this, and fly it over there".
 *
 * ── Somewhere they can actually get to ──────────────────────────────────────
 *
 * A `close` destination, which is a hop across this system where the system has
 * a second station and a short jump where it does not. Both are inside a
 * starter's reach, which is the requirement that matters — a first job that
 * needs a drive they have not bought yet is a wall.
 *
 * ── And it does not rotate ──────────────────────────────────────────────────
 *
 * Ordinary offers are keyed on the two-hour board era, so a board looked at
 * before and after a rollover is a different board. That is right for work and
 * wrong for this: a new player may take an hour to fly in, and arriving to find
 * the job they were sent for has been replaced would be baffling. The stream is
 * keyed on the station alone, so the answer is the same for as long as the
 * account is being introduced.
 */
export function tutorialBoard(seed, systemId, stationId) {
    const navigator = new Navigator(seed);
    const random = Rng.forEntity(seed, stationId, 'tutorial-delivery');
    /*
     * Rolled until it is somewhere other than here.
     *
     * `pickDestination` may legitimately answer with this station — a close job
     * is allowed to be a hop across the system, and this system may only have the
     * one — so the retry is how "somewhere else" is expressed. Bounded, because a
     * seeded stream that keeps answering the same way would otherwise spin.
     */
    for (let attempt = 0; attempt < 12; attempt++) {
        const destination = pickDestination(seed, navigator, systemId, 'close', random);
        if (!destination || destination.stationId === stationId)
            continue;
        return [tutorialOffer(destination)];
    }
    // Nowhere within reach. Vanishingly unlikely — the introduction only ever
    // starts in the core, where systems are close together — and an empty board
    // is a better answer than a job that cannot be completed.
    return [];
}
//# sourceMappingURL=missionBoard.js.map